import { Directive, Output, AfterContentInit, OnDestroy, ContentChildren, QueryList, EventEmitter } from '@angular/core';
import { NgModel } from '@angular/forms';
import { Subject } from 'rxjs';
import { Subscription } from 'rxjs';

/**
 * @whatItDoes Watches all child {@link NgModel} instances for validity changes.
 * supports models being added/removed (i.e. ngIf and ngFor) dynamically.
 *
 * Use this directive when you can't use a <form> to wrap input fields.
 * This also emits a onValidChange event which is useful for reacting to.
 *
 * @howToUse
 *
 * <div wl-ng-models-valid (onValidChange)="handleValidChange($event)">
 *
 * in the component class:
 * public handleValidChange(newValidValue: boolean) { doSomething(); }
 */
@Directive({
  selector: '[wl-ng-models-valid]',
  standalone: false,
})
export class WlNgModelsValidDirective implements AfterContentInit, OnDestroy {
  /*
   * Readonly.
   */
  public get valid(): boolean {
    return this._valid === null ? false : this._valid;
  }

  /*
   * emitted when valid changes
   */
  @Output() public onValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ContentChildren(NgModel) public _ngModels: QueryList<NgModel> = null;

  public _queryListSubscription: Subscription;
  public _ngModelSubscriptions: Array<Subscription> = [];
  public _valid: boolean = null;

  /**
   * construct
   */
  constructor() {}

  /**
   * subsribe to list change after view is initted
   */
  public ngAfterContentInit(): void {
    setTimeout(() => {
      this._queryListSubscription = this._ngModels.changes.subscribe(x => this.handleNgModleListMutate());
      this.handleNgModleListMutate();
    });
  }

  /**
   * cleanup
   */
  public ngOnDestroy(): void {
    // unsubscribe from everything. Memory leaks save!
    if (this._queryListSubscription) this._queryListSubscription.unsubscribe();

    this.unsubscribeFromNgModels();
  }

  /**
   * subscribe to all ngModels' invalidate event.
   * The invalidate event may be called multiple times (like when someone is typing),
   * so the validate method has it'self on a debounce.
   */
  public subscribeToNgModelsInvalidate() {
    let timeoutHandleId: number = 0;

    // single handler reference
    let validChangeHandlerDelegate = (valid: boolean) => {
      if (timeoutHandleId) clearTimeout(timeoutHandleId);

      // expensive processing and happens on every key press on text fields, so mitigate by debouncing
      timeoutHandleId = window.setTimeout(() => this.reevaluateValidity(), 200);
    };

    // subscribe to the invalid event type and save the subscriptions for unsubscribing later
    this._ngModels
      .map(ngModel =>
        ngModel.statusChanges
          //.map(x => x == 'INVALID')
          .subscribe(validChangeHandlerDelegate)
      )
      .forEach(sub => this._ngModelSubscriptions.push(sub));
  }

  /**
   * cleans up model subscriptions
   */
  public unsubscribeFromNgModels() {
    // unsubscribe from saved subscriptions
    if (this._ngModelSubscriptions) while (this._ngModelSubscriptions.length) this._ngModelSubscriptions.pop().unsubscribe();
    else this._ngModelSubscriptions = [];
  }

  /**
   * Evaliate validity of known ngModels. If validity changes, emits it with onValid
   */
  public reevaluateValidity() {
    let valid = true;

    if (this._ngModels && this._ngModels.length) valid = !this._ngModels.some(m => m.invalid);

    if (this._valid !== valid) {
      this._valid = valid;
      this.onValidChange.emit(this._valid);
    }
  }

  /**
   * called when the ngModel list changes (one is added, one is removed) but no info is provided (what is added, what is removed).
   * so clear subscriptions and re-add
   */
  public handleNgModleListMutate() {
    this.unsubscribeFromNgModels();
    this.subscribeToNgModelsInvalidate();
  }
}
